import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderableItem, OrderedItem, SizePrice, Upsell, UpsellWrapper, OrderedIngredient, ConfigService, StoreConfig } from 'ngx-web-api';
import { Observable, of, Subscription, noop } from 'rxjs';
import { map, mergeMap, filter, take } from 'rxjs/operators';
import { ItemEditorService } from '../../core/services/item-editor.service';
import { EditingItemContext } from '../../domain/editing-item-context';
import { ThemeService } from '../../core/services/theme.service';
import { Theme } from '../../domain/theme.model';
import { EditorComponent } from '../editor/editor.component';
import { ViewportService } from 'app/core/services/viewport.service';
import { OrderUpsellService } from 'app/core/services/order-upsell.service';

@Component({
  selector: 'fts-orderable-editor',
  templateUrl: './orderable-editor.component.html',
  styleUrls: ['./orderable-editor.component.scss'],
})
export class OrderableEditorComponent implements OnInit, OnDestroy {
  @Input()
  inItemEditorModal = false;

  private subscriptions: Subscription[];
  @ViewChild(EditorComponent, { static: true })
  editor: EditorComponent;

  orderableItem: OrderableItem;
  orderedItem: OrderedItem;
  theme: Theme;
  upsellItems: Upsell[] = [];
  selectedSizePrice: SizePrice;
  storeConfig: StoreConfig;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private itemEditorService: ItemEditorService,
    private themeService: ThemeService,
    private configService: ConfigService,
    public viewportService: ViewportService,
    private orderUpsellService: OrderUpsellService
  ) {}

  ngOnInit() {
    this.subscriptions = [
      this.initEditingItem(),
      this.itemEditorService.editingOrderableItem$.subscribe((orderable: OrderableItem) => {
        if (!!orderable) {
          this.orderableItem = orderable.copy();
        }
      }),
      this.themeService.theme.subscribe(theme => (this.theme = theme)),
      this.itemEditorService.editingItemSelectedSizePrice$.pipe(filter(sizePrice => !!sizePrice)).subscribe((sizePrice: SizePrice) => {
        this.selectedSizePrice = sizePrice;
        if (this.orderedItem) {
          this.orderedItem.size = sizePrice.size;
        }
      }),
      this.configService.storeConfig$.pipe(take(1)).subscribe(
        config => (this.storeConfig = config),
        () => noop
      ),
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  public canLeaveEditor(): boolean {
    if (!!this.editor) {
      return this.editor.canLeaveEditor();
    }

    return true;
  }

  public onChangeSizeTriggerUpsells({ orderableItem, orderedItem }: { orderableItem: OrderableItem; orderedItem: OrderedItem }) {
    this.getUpsellItems(orderableItem, orderedItem).subscribe(upsellItems => (this.upsellItems = upsellItems));
  }

  private initEditingItem(): Subscription {
    if (this.inItemEditorModal) {
      return this.itemEditorService.editingItem$
        .pipe(
          mergeMap((context: EditingItemContext) =>
            (context ? this.getUpsellItems(context.orderableItem, context.orderedItem) : of(null)).pipe(
              map(upsellItems => ({ context, upsellItems }))
            )
          ),
          take(1)
        )
        .subscribe(({ context, upsellItems }) => {
          this.getOrderedItemAndUpsells(context, upsellItems);
        });
    } else {
      return this.activatedRoute.data
        .pipe(
          map(data => data['context'] as EditingItemContext),
          mergeMap((context: EditingItemContext) =>
            (context ? this.getUpsellItems(context.orderableItem, context.orderedItem) : of(null)).pipe(
              map(upsellItems => ({ context, upsellItems }))
            )
          )
        )
        .subscribe(({ context, upsellItems }) => {
          if (!context) {
            this.router.navigate(['/menu']);
          } else {
            this.getOrderedItemAndUpsells(context, upsellItems);
          }
        });
    }
  }

  private getUpsellItems(item: OrderableItem, orderedItem: OrderedItem): Observable<Upsell[]> {
    return this.orderUpsellService.getUpsells(item, orderedItem).pipe(
      map((upsellWrappers: UpsellWrapper[]) => upsellWrappers.filter((upsellWrapper: UpsellWrapper) => !!upsellWrapper.items)),
      map((upsellWrappers: UpsellWrapper[]) => {
        if (upsellWrappers && upsellWrappers.length > 0) {
          return upsellWrappers[0].items;
        }
      })
    );
  }

  private getOrderedItemAndUpsells(context: EditingItemContext, upsellItems: any) {
    this.orderableItem = context.orderableItem.copy();
    this.orderedItem = context.orderedItem.copy();
    if (context.selectedUpfrontIngredient) {
      const newSelectedUpfrontIngredientRef = context.selectedUpfrontIngredient.copy();
      newSelectedUpfrontIngredientRef.qualifiers = context.selectedUpfrontIngredient.qualifiers.map(q => ({ ...q }));
      newSelectedUpfrontIngredientRef.qualifiers.forEach(q => (q.isDefault = false));
      const upfrontChoice = this.orderedItem.choices.find(ch => ch.name === context.unsatisfiedIngredientChoiceName);
      upfrontChoice.ingredients = [];
      // Mutate OrderableItem to set default pre-selected qualifiers
      newSelectedUpfrontIngredientRef.qualifiers
        .filter(q => !!context.selectedUpfrontQualifier.find(sq => sq === q.name))
        .forEach(q => (q.isDefault = true));
      upfrontChoice.ingredients.push(OrderedIngredient.createFromOrderable(newSelectedUpfrontIngredientRef, false, false));
    }
    if (upsellItems) {
      this.upsellItems = upsellItems;
    }
  }
}
